// Food search with two backends:
//
//   1. USDA FoodData Central — primary when REACT_APP_USDA_API_KEY is set.
//      Excellent for generic ingredients (eggs, chicken, rice) which is
//      the dominant query pattern for body-recomp tracking. Returns
//      standardized per-100g values for Foundation + SR Legacy datasets.
//
//   2. Open Food Facts — fallback. No API key required, but its search
//      is biased toward barcoded branded products, so generic queries
//      return junk. Kept as a no-config baseline.
//
// Caller doesn't care which one ran; both return the same normalized
// shape: { id, name, brand, kcal, protein, carbs, fat, imageUrl }.
//
// Attribution requirements:
//   - OFF: CC-BY-SA (already shown in FoodSearchField).
//   - USDA: public domain, no attribution required.

const USDA_API_KEY = process.env.REACT_APP_USDA_API_KEY
const USDA_ENDPOINT = 'https://api.nal.usda.gov/fdc/v1/foods/search'
const OFF_ENDPOINT = 'https://world.openfoodfacts.org/api/v2/search'

// Some OFF entries have garbage values (kcal in the thousands, or zero).
// Clamp to a reasonable range so the form doesn't auto-fill obvious nonsense.
function sanitizeKcal(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n < 0 || n > 9000) return null
  return Math.round(n)
}

function sanitizeMacro(v) {
  const n = Number(v)
  if (!Number.isFinite(n) || n < 0 || n > 999) return null
  return Math.round(n * 10) / 10
}

// Match the query's own script, not the UI language. Searching "Eggs"
// with lc=ar surfaces random Arabic-country products instead of egg
// matches; searching "بيض" should use lc=ar to return Arabic names.
const ARABIC_CHARS = /[؀-ۿݐ-ݿ]/

function detectLang(query) {
  return ARABIC_CHARS.test(query) ? 'ar' : 'en'
}

async function searchOff(query, signal) {
  const params = new URLSearchParams({
    search_terms: query,
    fields: 'code,product_name,product_name_en,product_name_ar,brands,nutriments,image_small_url',
    page_size: '10',
    lc: detectLang(query),
  })

  let res
  try {
    res = await fetch(`${OFF_ENDPOINT}?${params.toString()}`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (err) {
    if (err?.name === 'AbortError') return []
    return []
  }
  if (!res.ok) return []

  let data
  try { data = await res.json() } catch { return [] }
  const products = Array.isArray(data?.products) ? data.products : []

  const preferredName = detectLang(query) === 'ar'
    ? (p) => p.product_name_ar || p.product_name || p.product_name_en
    : (p) => p.product_name_en || p.product_name || p.product_name_ar

  return products
    .map((p) => {
      const name = (preferredName(p) || '').trim()
      if (!name) return null
      const n = p.nutriments || {}
      return {
        id: p.code,
        name,
        brand: (p.brands || '').split(',')[0]?.trim() || null,
        kcal: sanitizeKcal(n['energy-kcal_100g'] ?? n.energy_kcal_100g),
        protein: sanitizeMacro(n.proteins_100g),
        carbs: sanitizeMacro(n.carbohydrates_100g),
        fat: sanitizeMacro(n.fat_100g),
        imageUrl: p.image_small_url || null,
      }
    })
    .filter((p) => p && p.kcal != null)
}

// Pull a nutrient value from USDA's foodNutrients array by partial
// match on the human-readable nutrientName. Case-insensitive.
function findNutrientValue(nutrients, needles) {
  if (!Array.isArray(nutrients)) return null
  for (const n of nutrients) {
    const label = (n.nutrientName || '').toLowerCase()
    if (needles.some((needle) => label.includes(needle))) {
      return Number(n.value)
    }
  }
  return null
}

async function searchUsda(query, signal) {
  if (!USDA_API_KEY) return null  // null = "not configured, caller should try OFF"

  const params = new URLSearchParams({
    api_key: USDA_API_KEY,
    query,
    // Foundation + SR Legacy give standardized per-100g values. Branded
    // foods use per-serving sizes which would require extra normalization.
    dataType: 'Foundation,SR Legacy',
    pageSize: '10',
  })

  let res
  try {
    res = await fetch(`${USDA_ENDPOINT}?${params.toString()}`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (err) {
    if (err?.name === 'AbortError') return []
    return []
  }
  if (!res.ok) return []

  let data
  try { data = await res.json() } catch { return [] }
  const foods = Array.isArray(data?.foods) ? data.foods : []

  return foods
    .map((f) => {
      const name = (f.description || '').trim()
      if (!name) return null
      const nutrients = f.foodNutrients || []
      return {
        id: String(f.fdcId),
        name,
        brand: f.brandName || f.brandOwner || null,
        kcal: sanitizeKcal(findNutrientValue(nutrients, ['energy'])),
        protein: sanitizeMacro(findNutrientValue(nutrients, ['protein'])),
        carbs: sanitizeMacro(findNutrientValue(nutrients, ['carbohydrate'])),
        fat: sanitizeMacro(findNutrientValue(nutrients, ['total lipid', 'total fat', 'lipid'])),
        imageUrl: null,
      }
    })
    .filter((f) => f && f.kcal != null)
}

export async function searchFoods(query, { signal } = {}) {
  const q = (query || '').trim()
  if (q.length < 2) return []

  // USDA is better for generic ingredients. Try it first when the key is
  // configured; only fall back to OFF if USDA returned nothing (key missing
  // OR a query USDA doesn't know about, e.g. a regional packaged product).
  const usdaResults = await searchUsda(q, signal)
  if (usdaResults && usdaResults.length > 0) return usdaResults

  return searchOff(q, signal)
}
